import { useEffect, useRef, useState } from "react";
import Map, { Source, Layer } from "react-map-gl";
import Layers from "../../assets/img/icons/layers.png";
import Close from "../../assets/img/icons/cancel.png";
import Recenter from "../../assets/img/icons/recenter.png";
import Loader from "../../helpers/Loader";
import "simplebar-react/dist/simplebar.min.css";
import {
  distributionStatuses,
  mapStyles,
  mapboxDrawStyles,
} from "../../helpers/Constants";
import { registerKeyboardShortcuts } from "../../helpers/KeyboardShortcuts";
import MapStyleSelector from "../../helpers/MapStyleSelector";
import { LngLatBounds } from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { ReactSVG } from "react-svg";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "../../assets/img/icons/trash.svg";
import DrawIcon from "../../assets/img/icons/polygon.png";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

const BaseMapComponent = ({
  mapToolHeight,
  mapData,
  loading,
  innerRef,
  drawRef,
  children,
  onVisibleMarkersChange,
  setMapZoom,
  onClick,
  sourceComponent = "",
  drawMode,
  setDrawMode,
  onDrawComplete,
  createReshootBlock,
  visibleBlocks = {},
  highlightedIds = [],
  editModeBlock = null,
  structuresToAdd = [],
  structuresToRemove = [],
}) => {
  const isDistributionMap =
    sourceComponent === "opshub" || sourceComponent === "block-management";

  // if parent doesn't pass a draw ref, use internal ref
  const internalDrawRef = useRef(null);
  const effectiveDrawRef = drawRef || internalDrawRef;

  // state to hold the last drawn polygon (if we want to implement multiple polygons in future)
  const [drawnFeature, setDrawnFeature] = useState(null);

  const [selectedLayer, setSelectedLayer] = useState(
    localStorage.getItem("selectedMapStyle") || "light"
  );
  const [isLayerVisible, setIsLayerVisible] = useState(false);

  const [viewport, setViewport] = useState({
    latitude: 0,
    longitude: 0,
    zoom: 14,
  });
  const [currentZoom, setCurrentZoom] = useState(14);

  // Convert nearby_structures to GeoJSON format
  const getGeoJSONData = () => ({
    type: "FeatureCollection",
    features: mapData?.nearby_structures
      ? mapData.nearby_structures.map((structure) => {
          const statusColor =
            distributionStatuses?.[structure.status]?.color || "#007cbf";

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [structure.lon, structure.lat],
            },
            properties: {
              identifier: structure.identifier,
              severity: structure.severity,
              status: structure.status,
              color: statusColor,
            },
          };
        })
      : mapData?.map((structure) => {
          const isBeingAdded = structuresToAdd.includes(structure.id);
          const isBeingRemoved = structuresToRemove.includes(structure.id);
          const isInEditBlock =
            editModeBlock?.id && structure.block?.id === editModeBlock?.id;
          const isInBlockManagement = sourceComponent === "block-management";

          const getStatusColor = () => {
            if (isInBlockManagement) {
              if (editModeBlock?.id) {
                if (isBeingRemoved) return "#808080";
                if (isBeingAdded || isInEditBlock) {
                  return (
                    structure.block?.color || editModeBlock?.color || "#808080"
                  );
                }
                return structure.block?.color || "#808080";
              }
              return structure?.block?.color || "#808080";
            }

            return distributionStatuses?.[structure.status]?.color || "#007cbf";
          };
          const statusColor = getStatusColor();

          const shouldHighlight = (() => {
            if (isBeingRemoved) return false;
            return (
              isBeingAdded ||
              isInEditBlock ||
              highlightedIds.includes(structure.id)
            );
          })();
          const isHighlighted = shouldHighlight;

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [structure?.location?.lon, structure?.location?.lat],
            },
            properties: {
              status: structure.status,
              color: statusColor,
              opacity:
                visibleBlocks[structure.block?.id] || !structure?.block
                  ? 0.8
                  : 0,
              highlight: isHighlighted ? 1 : 0,
            },
          };
        }) || [],
  });

  const unclusteredPointLayer = {
    id: "unclustered-point",
    type: "circle",
    paint: {
      "circle-radius": 6,
      "circle-color": ["get", "color"],
      "circle-opacity": ["get", "opacity"],
      "circle-stroke-color": [
        "case",
        ["==", ["get", "highlight"], 1],
        "orange",
        "transparent",
      ],
      "circle-stroke-width": 2.5,
    },
  };

  const handleShowLayers = () => {
    setIsLayerVisible((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem("selectedMapStyle", selectedLayer);
  }, [selectedLayer]);

  const updateVisibleMarkers = (bounds) => {
    const visible = mapData?.nearby_structures
      ? mapData.nearby_structures.filter((structure) =>
          bounds.contains([structure.lon, structure.lat])
        )
      : mapData?.filter((structure) =>
          bounds.contains([structure?.location?.lon, structure?.location?.lat])
        );

    onVisibleMarkersChange(visible || []);
  };

  const handleZoomChange = (evt) => {
    const { viewState } = evt;
    setViewport(viewState);
    setCurrentZoom(viewState.zoom.toFixed(2));
    setMapZoom(viewState.zoom.toFixed(2));
    if (innerRef.current) {
      const bounds = innerRef.current.getBounds();
      updateVisibleMarkers(bounds);
    }
  };

  const handleMoveEnd = () => {
    if (innerRef.current) {
      const bounds = innerRef.current.getBounds();
      updateVisibleMarkers(bounds);
    }
  };

  const handleMapLoad = (evt) => {
    // evt.target is the Mapbox GL JS map instance
    const map = evt.target;

    // initialize draw for opshub and block-management maps
    effectiveDrawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      styles: mapboxDrawStyles,
    });
    map.addControl(effectiveDrawRef.current);

    // allow for keyboard shortcuts
    const canvas = map.getCanvas();
    canvas.tabIndex = 0;
    canvas.style.outline = "none";
    canvas.focus();

    const unregisterShortcuts = registerKeyboardShortcuts({
      targetElement: canvas,
      shortcuts: [
        {
          keys: ["d", "D"],
          action: () => {
            effectiveDrawRef.current.changeMode("draw_polygon");
            canvas.focus();
            setDrawMode(true);
          },
        },
        {
          keys: ["escape"],
          action: () => {
            effectiveDrawRef.current.changeMode("simple_select");
            canvas.focus();
            setDrawMode(false);
          },
        },
        {
          keys: ["delete", "backspace"],
          action: () => {
            effectiveDrawRef.current
              .getSelectedIds()
              .forEach((id) => effectiveDrawRef.current.delete(id));
            updateDrawn();
            canvas.focus();
            setDrawMode(false);
          },
        },
      ],
    });

    map.on("remove", unregisterShortcuts);

    // override default trash undo behavior so that trash btn always deletes
    const container = innerRef.current.getContainer();
    const trashBtn = container.querySelector(".mapbox-gl-draw_trash");

    if (trashBtn) {
      // remove any existing listener to avoid double-wiring
      trashBtn.replaceWith(trashBtn.cloneNode(true));
      const freshTrash = container.querySelector(".mapbox-gl-draw_trash");

      freshTrash.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();

        effectiveDrawRef.current.deleteAll();
        setDrawnFeature(null);
        onDrawComplete?.([]);
      });
    }

    // listen for create/update/delete
    const updateDrawn = () => {
      const all = effectiveDrawRef.current.getAll();
      if (all.features.length > 0) {
        // assuming one polygon at a time, get first polygon
        const feature = all.features[0];
        setDrawnFeature(feature);
        const ring = feature.geometry.coordinates[0] || [];
        onDrawComplete?.(ring);
      } else {
        setDrawnFeature(null);
        onDrawComplete?.([]);
      }
    };

    // allow only one polygon at a time
    map.on("draw.create", (e) => {
      const newFeature = e.features[0];
      effectiveDrawRef.current.getAll().features.forEach((f) => {
        if (f.id !== newFeature.id) {
          effectiveDrawRef.current.delete(f.id);
        }
      });
      updateDrawn();
    });

    map.on("draw.update", updateDrawn);
    map.on("draw.delete", updateDrawn);

    map.on("draw.modechange", ({ mode }) => {
      setDrawMode(mode === "draw_polygon");
    });

    handleRecenter();
  };

  const handleRecenter = () => {
    if (!mapData || !innerRef.current) return;
    const map = innerRef.current.getMap();
    const { lon, lat } = mapData.current_structure || mapData[0].location;
    const bounds = new LngLatBounds(
      [lon - 0.00135, lat - 0.00135],
      [lon + 0.00135, lat + 0.00135]
    );
    map.fitBounds(bounds, { animate: true, padding: 40, duration: 100 });
    updateVisibleMarkers(bounds);
  };

  // update styling of draw polygon button when in draw mode
  useEffect(() => {
    if (!innerRef.current) return;
    const container = innerRef.current.getContainer();
    const ctrlGroup = container.querySelector(".mapboxgl-ctrl-group");
    if (!ctrlGroup) return;

    ctrlGroup.classList.toggle("draw-mode", drawMode);
  }, [drawMode]);

  // ensure map centers around relevant points
  useEffect(() => {
    if (mapData && innerRef.current) {
      handleRecenter();
    }
  }, [mapData]);

  return (
    <>
      <div
        style={{
          width: "100%",
          height: mapToolHeight,
          position: "relative",
        }}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <button className="layers-button" onClick={handleShowLayers}>
              <img
                src={!isLayerVisible ? Layers : Close}
                alt="layers"
                style={{ width: "25px", padding: "3px" }}
              />
            </button>
            <button
              className={`${
                sourceComponent === "opshub" ||
                sourceComponent === "block-management"
                  ? "recenter-button"
                  : sourceComponent === "navigation"
                  ? "recenter-button-navigation"
                  : "recenter-button-reassign"
              }`}
              onClick={handleRecenter}
            >
              <img
                src={Recenter}
                alt="recenter"
                style={{ width: "25px", padding: "3px" }}
              />
            </button>
            {sourceComponent === "block-management" && (
              <button
                className="reshoot-button"
                onClick={createReshootBlock}
                disabled={mapData?.every((item) => item.status !== 6)}
              >
                Reshoot
              </button>
            )}
            {isDistributionMap && (
              <>
                <Tooltip title="Draw polygon [d]" placement="top" arrow>
                  <button
                    className={`draw-polygon-btn mapbox-ctrl-btn ${
                      drawMode ? "draw-mode" : ""
                    }`}
                    onClick={() => {
                      effectiveDrawRef.current?.changeMode("draw_polygon");
                      setDrawMode(!drawMode);
                    }}
                  >
                    <img
                      src={DrawIcon}
                      alt="polygon icon"
                      className="draw-polygon-icon"
                    />
                  </button>
                </Tooltip>
                <Tooltip title="Delete polygon [bksp]" placement="top" arrow>
                  <button
                    className="delete-polygon-btn mapbox-ctrl-btn"
                    onClick={() => {
                      effectiveDrawRef.current?.deleteAll();
                      setDrawnFeature(null);
                      onDrawComplete?.([]);
                      setDrawMode(false);
                    }}
                  >
                    <ReactSVG src={DeleteIcon} />
                  </button>
                </Tooltip>
              </>
            )}

            {isLayerVisible && (
              <div className="map-style-selector">
                <MapStyleSelector
                  setSelectedLayer={setSelectedLayer}
                  selectedLayer={selectedLayer}
                />
              </div>
            )}

            <Map
              ref={innerRef}
              initialViewState={viewport}
              onMove={handleZoomChange}
              onLoad={handleMapLoad}
              style={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                borderRadius: "8px",
              }}
              mapStyle={mapStyles[selectedLayer].mapUrl}
              onMoveEnd={handleMoveEnd}
              attributionControl={false}
              onClick={onClick}
            >
              {currentZoom <= 16 && mapData && (
                <Source id="unclustered" type="geojson" data={getGeoJSONData()}>
                  <Layer {...unclusteredPointLayer} />
                </Source>
              )}
              {children}
            </Map>
          </>
        )}
      </div>
    </>
  );
};

export default BaseMapComponent;
